import type { JSONContent } from '@tiptap/react';

import type { WebsiteBlock } from '@/api/schema';

type Runs = NonNullable<WebsiteBlock['runs']>;

export function articleLink(value: string) {
  try {
    const url = new URL(value);
    if (['http:', 'https:'].includes(url.protocol) && !url.username && !url.password) {
      return url.href;
    }
  } catch {
    return null;
  }
  return null;
}

function inlineNodes(value: string, runs?: Runs): JSONContent[] {
  return (runs?.length ? runs : [{ text: value, bold: false, italic: false }]).flatMap(
    (run) => {
      const marks = [
        ...(run.bold ? [{ type: 'bold' }] : []),
        ...(run.italic ? [{ type: 'italic' }] : []),
        ...(run.href ? [{ type: 'link', attrs: { href: run.href } }] : []),
      ];
      return run.text
        .split('\n')
        .flatMap((line, index) => [
          ...(index > 0 ? [{ type: 'hardBreak' }] : []),
          ...(line ? [{ type: 'text', text: line, marks }] : []),
        ]);
    },
  );
}

export function articleDocument(
  body: WebsiteBlock[],
  imageUrl: (id: string) => string,
): JSONContent {
  return {
    type: 'doc',
    content: body.map((block) => {
      if (block.type === 'image') {
        return {
          type: 'image',
          attrs: {
            src: imageUrl(block.image_id ?? ''),
            imageId: block.image_id,
            alt: block.alt,
          },
        };
      }
      if (block.type === 'list') {
        return {
          type: block.ordered ? 'orderedList' : 'bulletList',
          content: (block.items ?? []).map((item, index) => ({
            type: 'listItem',
            content: [
              { type: 'paragraph', content: inlineNodes(item, block.item_runs?.[index]) },
            ],
          })),
        };
      }
      return block.type === 'heading'
        ? { type: 'heading', attrs: { level: 2 }, content: inlineNodes(block.text, block.runs) }
        : { type: 'paragraph', content: inlineNodes(block.text, block.runs) };
    }),
  };
}

function inlineRuns(node: JSONContent): Runs {
  return (node.content ?? []).flatMap<Runs[number]>((child) => {
    if (child.type === 'hardBreak') return [{ text: '\n', bold: false, italic: false }];
    if (!child.text) return [];
    const href: unknown = child.marks?.find((mark) => mark.type === 'link')?.attrs?.href;
    return [
      {
        text: child.text,
        bold: child.marks?.some((mark) => mark.type === 'bold') ?? false,
        italic: child.marks?.some((mark) => mark.type === 'italic') ?? false,
        href: typeof href === 'string' ? articleLink(href) : null,
      },
    ];
  });
}

const textOf = (runs: Runs) =>
  runs
    .map((run) => run.text)
    .join('')
    .trim();

export function articleBlocks(document: JSONContent): WebsiteBlock[] {
  return (document.content ?? []).flatMap((node): WebsiteBlock[] => {
    if (node.type === 'image') {
      const image_id: unknown = node.attrs?.imageId;
      return typeof image_id === 'string'
        ? [
            {
              type: 'image',
              image_id,
              text: '',
              alt: String(node.attrs?.alt ?? ''),
              ordered: false,
            },
          ]
        : [];
    }
    if (node.type === 'bulletList' || node.type === 'orderedList') {
      const item_runs = (node.content ?? [])
        .flatMap((item) => (item.content ?? []).map(inlineRuns))
        .filter((runs) => textOf(runs));
      return item_runs.length
        ? [
            {
              type: 'list',
              items: item_runs.map(textOf),
              item_runs,
              ordered: node.type === 'orderedList',
              text: '',
              alt: '',
            },
          ]
        : [];
    }
    const runs = inlineRuns(node);
    const text = textOf(runs);
    return text
      ? [
          {
            type: node.type === 'heading' ? 'heading' : 'paragraph',
            text,
            runs,
            alt: '',
            ordered: false,
          },
        ]
      : [];
  });
}
